"use client";

import { useEffect } from "react";
import { DISCORD_SUPPORT_URL, TWITTER_URL } from "@cede/utils";
import { useLayoutChromeStore } from "@/store/layoutChromeStore";
import { Globe } from "lucide-react";

/**
 * Shown by `proxy.ts` to visitors in a blocked region.
 *
 * Hub's root layout owns the app chrome, so the page hides it through the same store
 * the PnL competition pages use rather than being given its own root layout — that
 * would mean splitting the layout across every route in the app. Geometry and type
 * scale follow `not-found`, the app's other standalone surface: `flex-1 w-full` is
 * what centres it inside the shell, which a bare `min-h-screen` does not.
 */
export function RegionBlockedContent() {
  const setHideChrome = useLayoutChromeStore((state) => state.setHideChrome);
  const setHideMobileNav = useLayoutChromeStore((state) => state.setHideMobileNav);

  // Synchronising with a global store, which is what an effect is for. Restored on
  // unmount so a visitor who is later allowed through doesn't land on a chrome-less app.
  useEffect(() => {
    setHideChrome(true);
    setHideMobileNav(true);
    return () => {
      setHideChrome(false);
      setHideMobileNav(false);
    };
  }, [setHideChrome, setHideMobileNav]);

  return (
    <div className="flex min-h-[100dvh] w-full flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-8 flex size-20 items-center justify-center rounded-full border border-cede-border bg-cede-bg-component">
        <Globe className="size-9 text-cede-primary" aria-hidden="true" />
      </div>

      <h2 className="h5 text-cede-text-primary">Not available in your region</h2>
      <p className="body1 mt-2 max-w-md text-cede-text-secondary">
        Cede Hub can&apos;t be used from your current location.
      </p>
      <p className="body1 mt-1 max-w-md text-cede-text-secondary">
        Holding a balance? Contact the team and we&apos;ll sort it out.
      </p>

      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
        <a
          href={DISCORD_SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-large inline-flex h-[42px] items-center justify-center rounded-button bg-cede-button-primary px-4 text-cede-text-primary transition-all hover:bg-cede-button-primary-hover"
        >
          Contact us on Discord
        </a>
        <a
          href={TWITTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-large inline-flex h-[42px] items-center justify-center rounded-button border border-cede-border px-4 text-cede-text-primary transition-all hover:bg-cede-bg-hover"
        >
          Contact us on X
        </a>
      </div>
    </div>
  );
}
